import type { StatementReferencesPatch } from '../model/providedReferences';
import type { StatementReferenceState } from '../model/statementReferenceState';

import type { components } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

type MessageStatementReferences = components['schemas']['MessageStatementReferences'];

/**
 * A message's entry as the chat looks it up. The store can hold an entry for
 * a message Twilio never acknowledged, which has no SID and nothing to draw
 * against, so it maps to `undefined`.
 */
export function toStatementReferenceState(
  wire: MessageStatementReferences
): StatementReferenceState | undefined {
  if (!wire.messageSid) {
    return undefined;
  }

  return {
    messageSid: wire.messageSid,
    authorId: wire.authorId,
    approved: wire.approved,
    extractedStatements: wire.statements,
    attachedStatementReferences: wire.attachedReferences,
    statementCitationStatuses: wire.citationStatuses,
    statementReferenceHelpRequests: wire.helpRequests,
  };
}

export async function listStatementReferences(
  conversationId: string
): Promise<StatementReferenceState[]> {
  const response = await fetchClient.GET(
    '/v2/statement-references/conversations/{conversationId}',
    { params: { path: { conversationId } } }
  );

  return (response.data?.data ?? []).flatMap((wire) => toStatementReferenceState(wire) ?? []);
}

/**
 * Replaces each array the patch carries, whole, and returns the message's
 * state as the server now holds it. The server stamps a help request's
 * `createdAt`, so the client sends only what the author wrote.
 */
export async function updateStatementReferences(
  conversationId: string,
  messageSid: string,
  patch: StatementReferencesPatch
): Promise<StatementReferenceState> {
  const response = await fetchClient.PUT('/v2/statement-references/messages/{messageSid}', {
    params: { path: { messageSid } },
    body: {
      conversationId,
      attachedReferences: patch.attachedStatementReferences,
      citationStatuses: patch.statementCitationStatuses,
      helpRequests: patch.statementReferenceHelpRequests?.map(
        ({ statementIndex, description }) => ({ statementIndex, description })
      ),
    },
  });

  const state = response.data?.data && toStatementReferenceState(response.data.data);

  if (!state) {
    throw new Error('Statement references update returned empty response');
  }

  return state;
}
